import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Heading,
  Text,
  HStack,
  VStack,
  SimpleGrid,
  Badge,
  Spinner,
  Center,
  Card,
  CardBody,
  Divider,
  Select,
  Tooltip,
} from "@chakra-ui/react";
import axios from "axios";
import proxy from "../../proxy";

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const fmtMoney = (n) =>
  `$${Number(n).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const monthLabelFromKey = (key) => {
  const [y, m] = key.split("-").map(Number);
  return `${MONTH_NAMES[m - 1]} ${y}`;
};

const daysAwayBadge = (days) => {
  if (days <= 7)  return { label: `in ${days}d`, scheme: "green" };
  if (days <= 30) return { label: `in ${days}d`, scheme: "teal" };
  if (days <= 60) return { label: `in ${days}d`, scheme: "blue" };
  return { label: `in ${days}d`, scheme: "gray" };
};

const DividendCalendar = () => {
  const [payments, setPayments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [months, setMonths] = useState(6);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    axios
      .get(`${proxy}server_mts/GetDividendCalendar.php?months=${months}`, {
        headers: { "Cache-Control": "no-cache" },
      })
      .then((res) => {
        if (cancelled) return;
        setPayments(Array.isArray(res.data) ? res.data : []);
        setError(null);
      })
      .catch(() => {
        if (cancelled) return;
        setError("Unable to load dividend calendar.");
      })
      .finally(() => !cancelled && setIsLoading(false));

    return () => { cancelled = true; };
  }, [months]);

  // Group payments by month_key, preserving chronological order.
  const grouped = useMemo(() => {
    const map = new Map();
    for (const p of payments) {
      if (!map.has(p.month_key)) {
        map.set(p.month_key, { key: p.month_key, total: 0, items: [] });
      }
      const bucket = map.get(p.month_key);
      bucket.items.push(p);
      bucket.total += Number(p.expected_amount || 0);
    }
    return Array.from(map.values());
  }, [payments]);

  const horizonTotal = useMemo(
    () => payments.reduce((sum, p) => sum + Number(p.expected_amount || 0), 0),
    [payments]
  );

  const monthlyAvg = grouped.length > 0 ? horizonTotal / grouped.length : 0;

  if (isLoading) {
    return (
      <Center py={10}>
        <VStack spacing={3}>
          <Spinner size="lg" color="teal.300" thickness="3px" />
          <Text fontSize="sm" color="gray.400">Loading dividend calendar...</Text>
        </VStack>
      </Center>
    );
  }

  if (error) {
    return (
      <Center py={10}>
        <VStack spacing={2}>
          <Text fontSize="3xl">📅</Text>
          <Text color="red.300" fontSize="sm">{error}</Text>
        </VStack>
      </Center>
    );
  }

  return (
    <Box
      p={{ base: 3, md: 4 }}
      borderWidth="1px"
      borderColor="gray.700"
      borderRadius="lg"
      boxShadow="lg"
      width="100%"
      bg="gray.800"
    >
      {/* Horizon selector + totals row */}
      <HStack
        justify="space-between"
        align={{ base: "flex-start", md: "center" }}
        flexDirection={{ base: "column", md: "row" }}
        mb={4}
        spacing={3}
      >
        <HStack spacing={2}>
          <Text fontSize="xs" color="gray.500">Horizon:</Text>
          <Select
            size="sm"
            width="auto"
            value={months}
            onChange={(e) => setMonths(Number(e.target.value))}
            bg="gray.900"
            borderColor="gray.700"
            color="teal.200"
          >
            <option value={3}>3 months</option>
            <option value={6}>6 months</option>
            <option value={12}>12 months</option>
          </Select>
        </HStack>

        <HStack
          spacing={{ base: 4, md: 6 }}
          flexWrap="wrap"
          justify={{ base: "flex-start", md: "flex-end" }}
        >
          <VStack spacing={0} align="flex-start">
            <Text fontSize="2xs" color="gray.500">Expected total</Text>
            <Text fontSize="md" fontWeight="bold" color="green.300">
              {fmtMoney(horizonTotal)}
            </Text>
          </VStack>
          <VStack spacing={0} align="flex-start">
            <Text fontSize="2xs" color="gray.500">Avg / month</Text>
            <Text fontSize="md" fontWeight="bold" color="teal.300">
              {fmtMoney(monthlyAvg)}
            </Text>
          </VStack>
          <VStack spacing={0} align="flex-start">
            <Text fontSize="2xs" color="gray.500">Payments</Text>
            <Text fontSize="md" fontWeight="bold" color="purple.300">
              {payments.length}
            </Text>
          </VStack>
        </HStack>
      </HStack>

      <Text fontSize="2xs" color="gray.500" mb={3}>
        Estimated from last recorded payment date and dividend frequency.
        Real ex-div dates may shift by a few days.
      </Text>

      {grouped.length === 0 ? (
        <Center py={10}>
          <VStack spacing={2}>
            <Text fontSize="3xl">🗓️</Text>
            <Text fontSize="sm" color="gray.400">
              No estimated payments in the selected horizon.
            </Text>
          </VStack>
        </Center>
      ) : (
        <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
          {grouped.map((bucket) => (
            <Card
              key={bucket.key}
              bg="gray.900"
              borderWidth="1px"
              borderColor="gray.700"
              boxShadow="md"
              _hover={{ borderColor: "teal.600" }}
              transition="all 0.2s"
            >
              <CardBody p={4}>
                <HStack justify="space-between" mb={2}>
                  <Heading size="sm" color="teal.200">
                    {monthLabelFromKey(bucket.key)}
                  </Heading>
                  <Tooltip
                    hasArrow
                    label={`${bucket.items.length} payment(s) expected`}
                    placement="top"
                  >
                    <Badge colorScheme="teal" fontSize="xs">
                      {fmtMoney(bucket.total)}
                    </Badge>
                  </Tooltip>
                </HStack>

                <Divider borderColor="gray.700" mb={3} />

                <VStack spacing={2} align="stretch">
                  {bucket.items.map((p, idx) => {
                    const badge = daysAwayBadge(p.days_away);
                    return (
                      <Box
                        key={`${p.ticker}-${p.expected_date}-${idx}`}
                        p={2}
                        borderRadius="md"
                        bg="whiteAlpha.50"
                        _hover={{ bg: "whiteAlpha.100" }}
                      >
                        <HStack justify="space-between" align="flex-start">
                          <VStack spacing={0} align="flex-start" flex={1} minW={0}>
                            <HStack spacing={2}>
                              <Text
                                fontSize="sm"
                                fontWeight="bold"
                                color="white"
                              >
                                {p.ticker}
                              </Text>
                              <Badge
                                colorScheme={badge.scheme}
                                fontSize="2xs"
                                variant="subtle"
                              >
                                {badge.label}
                              </Badge>
                            </HStack>
                            <Text
                              fontSize="2xs"
                              color="gray.500"
                              noOfLines={1}
                              title={p.company}
                            >
                              {p.company}
                            </Text>
                            <Text fontSize="2xs" color="gray.400">
                              ~{p.expected_date} · {p.frequency_label}
                            </Text>
                          </VStack>

                          <VStack spacing={0} align="flex-end">
                            <Text fontSize="sm" fontWeight="bold" color="green.300">
                              {fmtMoney(p.expected_amount)}
                            </Text>
                            <Text fontSize="2xs" color="gray.500">
                              {p.nr_shares} sh × ${p.div_share}
                            </Text>
                          </VStack>
                        </HStack>
                      </Box>
                    );
                  })}
                </VStack>
              </CardBody>
            </Card>
          ))}
        </SimpleGrid>
      )}
    </Box>
  );
};

export default DividendCalendar;
